// Live password validation checklist component with smart positioning
import React, { useEffect, useRef, useState, useId } from 'react';
import { createPortal } from 'react-dom';
import { Check, X } from 'lucide-react';
import { usePasswordChecks } from '../../hooks/usePasswordChecks';
import '../../styles/password-checklist.css';

const PasswordChecklist = ({ password, show = true, inputRef, anchorRef, panelRef: externalPanelRef, id }) => {
  const generatedId = useId();
  const panelId = id || `password-requirements-${generatedId}`;
  const { checks } = usePasswordChecks(password);
  const internalPanelRef = useRef(null);
  const panelRef = externalPanelRef || internalPanelRef;
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, placement: 'below' });

  // Use anchorRef if provided, otherwise fall back to inputRef
  const activeAnchorRef = anchorRef || inputRef;

  useEffect(() => {
    if (!show || !activeAnchorRef?.current) return;

    const updatePosition = () => {
      const anchorRect = activeAnchorRef.current.getBoundingClientRect();
      const panelHeight = panelRef.current?.offsetHeight || 200; // Estimate if not rendered yet
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - anchorRect.bottom;
      const spaceAbove = anchorRect.top;

      // Determine if we should show above or below
      const placement = spaceBelow >= panelHeight + 20 ? 'below' :
                       spaceAbove >= panelHeight + 20 ? 'above' : 'below';

      const top = placement === 'below'
        ? anchorRect.bottom + 8
        : anchorRect.top - panelHeight - 8;

      setPosition({
        top,
        left: anchorRect.left,
        width: anchorRect.width,
        placement
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [show, activeAnchorRef, panelRef]);

  // ESC key handling moved to parent component (Signup.js) for better control

  if (!show) return null;

  const panel = (
    <div
      ref={panelRef}
      id={panelId}
      className={`password-checklist password-checklist--portal password-checklist--${position.placement}`}
      role="tooltip"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: `${position.width}px`,
        zIndex: 2000,
      }}
    >
      {checks.map((check) => (
        <div
          key={check.id}
          className={`password-check-item ${check.passed ? 'passed' : 'pending'}`}
        >
          <span className="password-check-icon">
            {check.passed ? (
              <Check size={14} aria-label="Requirement met" />
            ) : (
              <X size={14} aria-label="Requirement not met" />
            )}
          </span>
          <span className="password-check-label">{check.label}</span>
        </div>
      ))}
    </div>
  );

  return createPortal(panel, document.body);
};

export default PasswordChecklist;
